import React from 'react';
import mermaid from 'mermaid';
import NodeWithCodePanelWrapper from './NodeWithCodePanelWrapper';
import { parseJSONCleaned } from 'utilities/index';
import { useDebounce } from 'hooks/common';
import { Box } from '@mui/material';

// Initialize mermaid only once at the module level with specific settings
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  logLevel: 'fatal', // Only show fatal errors, not warnings
});

type MermaidProps = {
  initialContent: string | undefined;
  readOnly?: boolean;
  onChange?: (output: string) => Promise<void>;
  onError?: (errorMessage: string) => void;
};
function Mermaid({
  initialContent = '',
  readOnly,
  onChange,
  onError,
}: MermaidProps) {
  const diagramRef = React.useRef<HTMLDivElement>(null);
  const initialValue = parseJSONCleaned<string>(initialContent || '');
  const [mermaidContent, setMermaidContent] = React.useState(initialValue);
  const debouncedOnChange = useDebounce()(onChange ? onChange : () => {}, 500);

  // Ensure a stable, unique ID for this diagram instance
  const diagramId = React.useMemo(
    () => `mermaid-${Math.random().toString(36).substring(2, 12)}`,
    []
  );

  // Effect to handle content changes and rendering
  React.useEffect(() => {
    if (!diagramRef.current) return;

    // Clean up the container first
    diagramRef.current.innerHTML = '';

    const renderMermaid = async () => {
      try {
        // Process content through replacer function
        const processedContent = mermaidContent;

        if (!processedContent || processedContent.trim() === '') {
          if (diagramRef.current) {
            onError && onError('The Content to render diagram is empty.');
          }
          return;
        }

        // Create a container element
        const container = document.createElement('div');
        container.id = diagramId;
        container.style.width = '100%';
        container.style.height = '100%';

        // Add container to DOM
        if (diagramRef.current) {
          diagramRef.current.appendChild(container);
        }

        // Render with the latest mermaid API
        const { svg } = await mermaid.render(diagramId, processedContent);

        // Replace the container with the SVG
        if (diagramRef.current) {
          diagramRef.current.innerHTML = svg;

          // Fix SVG styles for better display
          const svgElement = diagramRef.current.querySelector('svg');
          if (svgElement) {
            svgElement.style.width = '100%';
            svgElement.style.maxWidth = '100%';
            svgElement.style.height = 'auto';
            svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            // Force SVG to respect container boundaries
            if (
              !svgElement.hasAttribute('viewBox') &&
              svgElement.hasAttribute('width') &&
              svgElement.hasAttribute('height')
            ) {
              const width = svgElement.getAttribute('width');
              const height = svgElement.getAttribute('height');
              svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
            }

            // Remove fixed dimensions if they exist
            svgElement.removeAttribute('width');
            svgElement.removeAttribute('height');
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          onError && onError(error.message);
          console.error('Failed to render mermaid diagram:', error);
        }
      }
    };

    // Slight delay to ensure the DOM is ready
    const timeoutId = setTimeout(() => {
      renderMermaid();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (diagramRef.current) {
        diagramRef.current.innerHTML = '';
      }
    };
  }, [mermaidContent, diagramId, onError]);

  const handleChange = (value: string) => {
    setMermaidContent(value);
    debouncedOnChange(value);
  };

  if (readOnly) {
    return (
      <Box
        bgcolor={(theme) => theme.palette.common.white}
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',

          '& svg': {
            maxWidth: '100%',
            width: '100%',
            height: 'auto',
          },
        }}
      >
        <div
          ref={diagramRef}
          style={{ width: '100%', height: '100%', overflow: 'hidden' }}
        />
      </Box>
    );
  }

  return (
    <NodeWithCodePanelWrapper
      initialContent={initialValue || ''}
      onChange={handleChange}
      readOnly={readOnly}
      language='javascript'
    >
      <div
        ref={diagramRef}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      />
    </NodeWithCodePanelWrapper>
  );
}

export default React.memo(Mermaid, (prevProps, nextProps) => {
  return prevProps.initialContent === nextProps.initialContent;
});
